import { memo } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import type { SemanticTone } from '@/theme/tokens';

export interface StatusBadgeProps {
  label: string;
  tone?: SemanticTone;
  /** `dot` adds a leading indicator – useful in dense tables. */
  variant?: 'solid' | 'soft' | 'dot';
  size?: 'small' | 'medium';
  tooltip?: string;
  /** Leading glyph; decorative, the label remains the accessible name. */
  icon?: ReactNode;
  /** Announced by screen readers instead of the visible label, when they differ. */
  ariaLabel?: string;
}

/**
 * The single status pill used across the app.
 *
 * Built on `Box` rather than MUI's `Chip` because a chip carries interaction
 * affordances (delete, click, focus ring) that a read-only status must not
 * imply. Colour always comes with a text label, so status is never conveyed by
 * hue alone (WCAG 1.4.1).
 */
export const StatusBadge = memo(function StatusBadge({
  label,
  tone = 'neutral',
  variant = 'soft',
  size = 'small',
  tooltip,
  icon,
  ariaLabel,
}: StatusBadgeProps): JSX.Element {
  const theme = useTheme();
  const colors = theme.tones[tone];
  const isSmall = size === 'small';

  const solid = variant === 'solid';

  const badge = (
    <Box
      component="span"
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isSmall ? 1 : 1.5,
        maxWidth: '100%',
        paddingInline: isSmall ? 1.5 : 2,
        height: isSmall ? 22 : 28,
        borderRadius: 1.5,
        fontSize: isSmall ? '0.6875rem' : '0.75rem',
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        border: '1px solid',
        backgroundColor: solid ? colors.main : colors.surface,
        borderColor: solid ? colors.main : colors.border,
        color: solid ? theme.palette.getContrastText(colors.main) : colors.contrastText,
      }}
    >
      {icon ? (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: 'inline-flex',
            flexShrink: 0,
            fontSize: isSmall ? 13 : 15,
            lineHeight: 0,
            '& > svg': { fontSize: 'inherit' },
          }}
        >
          {icon}
        </Box>
      ) : (
        variant === 'dot' && (
          <Box
            component="span"
            aria-hidden="true"
            sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.main, flexShrink: 0 }}
          />
        )
      )}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Box>
    </Box>
  );

  if (!tooltip) return badge;
  return (
    <Tooltip title={tooltip}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {badge}
      </Box>
    </Tooltip>
  );
});
